import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import Swal from "sweetalert2";

interface Entry {
  id: string;
  guest_name: string;
  message: string | null;
  emoji: string | null;
  pos_x: number;
  pos_y: number;
  signature_data: string | null;
  photo_url: string | null;
  status: string;
  created_at: string;
  photo?: { photo_url: string } | null;
}

interface Props {
  bookId: string;
  tenantId: string;
}

type FilterTab = "all" | "pending" | "visible" | "hidden";

const STATUS_META: Record<string, { label: string; className: string }> = {
  visible: { label: "Visible",  className: "bg-emerald-100 text-emerald-700" },
  hidden:  { label: "Oculto",   className: "bg-slate-100   text-slate-500"   },
  pending: { label: "Pendiente",className: "bg-amber-100   text-amber-700"   },
};

export default function MessagesTab({ bookId, tenantId }: Props) {
  const [entries,  setEntries]  = useState<Entry[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState<FilterTab>("all");

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("signature_book_entries")
      .select("*, photo:photo_id(photo_url)")
      .eq("signature_book_id", bookId)
      .order("created_at", { ascending: false });
    setEntries((data as Entry[]) ?? []);
    setLoading(false);
  }, [bookId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  /* ── Status helpers ─────────────────────────────── */
  const setStatus = async (entry: Entry, next: string) => {
    const { error } = await supabase
      .from("signature_book_entries")
      .update({ status: next })
      .eq("id", entry.id)
      .eq("signature_book_id", bookId);
    if (!error) setEntries((prev) => prev.map((e) => e.id === entry.id ? { ...e, status: next } : e));
  };

  const handleDelete = async (entry: Entry) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar mensaje?",
      text: `Se eliminará permanentemente el mensaje de "${entry.guest_name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });
    if (!isConfirmed) return;
    const { error } = await supabase
      .from("signature_book_entries")
      .delete()
      .eq("id", entry.id)
      .eq("signature_book_id", bookId);
    if (!error) setEntries((prev) => prev.filter((e) => e.id !== entry.id));
  };

  /* ── Bulk approve all pending ─────────────────── */
  const handleApproveAll = async () => {
    const pendingIds = entries.filter((e) => e.status === "pending").map((e) => e.id);
    if (pendingIds.length === 0) return;
    await supabase
      .from("signature_book_entries")
      .update({ status: "visible" })
      .in("id", pendingIds)
      .eq("signature_book_id", bookId);
    setEntries((prev) =>
      prev.map((e) => pendingIds.includes(e.id) ? { ...e, status: "visible" } : e),
    );
  };

  /* ── Derived counts ───────────────────────────── */
  const pendingCount = entries.filter((e) => e.status === "pending").length;
  const visibleCount = entries.filter((e) => e.status === "visible").length;
  const hiddenCount  = entries.filter((e) => e.status === "hidden").length;

  const filtered = filter === "all"
    ? entries
    : entries.filter((e) => e.status === filter);

  /* ── Tab definition ──────────────────────────── */
  const TABS: { id: FilterTab; label: string; count: number }[] = [
    { id: "all",     label: "Todos",     count: entries.length },
    { id: "pending", label: "Pendientes",count: pendingCount   },
    { id: "visible", label: "Visibles",  count: visibleCount   },
    { id: "hidden",  label: "Ocultos",   count: hiddenCount    },
  ];

  /* ─── Render ─────────────────────────────────── */
  return (
    <div className="space-y-5 max-w-3xl">

      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-slate-800 font-semibold text-sm">Moderación de mensajes</h3>
          <p className="text-slate-400 text-xs mt-0.5">
            Controla qué mensajes son visibles en el libro público y el PDF.
          </p>
        </div>
        {pendingCount > 0 && (
          <button
            onClick={handleApproveAll}
            className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-checkbox-multiple-line"></i>
            Aprobar todos ({pendingCount})
          </button>
        )}
      </div>

      {/* Pending alert banner */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 shrink-0">
            <i className="ri-time-line text-base"></i>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {pendingCount} mensaje{pendingCount !== 1 ? "s" : ""} esperando moderación
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              Revísalos y decide si aprobarlos o ocultarlos antes de que aparezcan en el portal del cliente.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              filter === tab.id
                ? "bg-white text-slate-700 shadow-sm"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                filter === tab.id
                  ? tab.id === "pending"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-500"
                  : "bg-slate-200 text-slate-400"
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-white rounded-2xl border border-slate-200 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400 mb-3">
            <i className="ri-message-3-line text-2xl"></i>
          </div>
          <p className="text-slate-500 font-medium text-sm">
            {filter === "all"
              ? "Sin mensajes aún"
              : `Sin mensajes ${filter === "visible" ? "visibles" : filter === "hidden" ? "ocultos" : "pendientes"}`}
          </p>
          <p className="text-slate-400 text-xs mt-1 max-w-xs leading-relaxed">
            {filter === "all"
              ? "Los mensajes aparecen cuando los invitados los dejan desde el libro público."
              : "Usa los filtros para ver otros estados."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => {
            const meta = STATUS_META[entry.status] ?? STATUS_META.hidden;
            return (
              <div
                key={entry.id}
                className={`bg-white rounded-2xl border p-4 transition-all ${
                  entry.status === "hidden"  ? "border-slate-200 opacity-60" :
                  entry.status === "pending" ? "border-amber-200 bg-amber-50/20" :
                                               "border-slate-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  {(entry.photo?.photo_url || entry.photo_url) && (
                    <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                      <img
                        src={entry.photo?.photo_url ?? entry.photo_url ?? ""}
                        alt=""
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{entry.guest_name}</span>
                      {entry.emoji && <span className="text-base leading-none">{entry.emoji}</span>}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.className}`}>
                        {meta.label}
                      </span>
                      {entry.signature_data && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600 flex items-center gap-1">
                          <i className="ri-quill-pen-line text-[9px]"></i>
                          Con firma
                        </span>
                      )}
                    </div>

                    {entry.message && (
                      <p className="text-slate-600 text-sm mt-1.5 leading-relaxed line-clamp-3">
                        {entry.message}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-2-line text-[10px]"></i>
                        {entry.pos_x.toFixed(0)}%, {entry.pos_y.toFixed(0)}%
                      </span>
                      <span>·</span>
                      <span>
                        {new Date(entry.created_at).toLocaleString("es-MX", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>

                    {entry.signature_data && (
                      <div className="mt-2 w-24 h-12 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                        <img src={entry.signature_data} alt="firma" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {/* Pending: approve / hide */}
                    {entry.status === "pending" && (
                      <>
                        <button
                          onClick={() => setStatus(entry, "visible")}
                          title="Aprobar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-check-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setStatus(entry, "hidden")}
                          title="Rechazar / Ocultar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-off-line text-sm"></i>
                        </button>
                      </>
                    )}

                    {/* Visible: hide */}
                    {entry.status === "visible" && (
                      <button
                        onClick={() => setStatus(entry, "hidden")}
                        title="Ocultar"
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-eye-off-line text-sm"></i>
                      </button>
                    )}

                    {/* Hidden: restore */}
                    {entry.status === "hidden" && (
                      <button
                        onClick={() => setStatus(entry, "visible")}
                        title="Restaurar"
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                    )}

                    {/* Delete — always available */}
                    <button
                      onClick={() => handleDelete(entry)}
                      title="Eliminar"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary footer */}
      {entries.length > 0 && !loading && (
        <div className="flex items-center gap-4 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
            {visibleCount} visibles
          </span>
          {pendingCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0"></span>
              {pendingCount} pendientes
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-slate-300 shrink-0"></span>
            {hiddenCount} ocultos
          </span>
          <span className="ml-auto text-slate-400 font-medium">{entries.length} total</span>
        </div>
      )}
    </div>
  );
}